import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAdminAuthStore from '../../stores/adminAuthStore';
import {
  createAdminJob,
  deleteAdminJob,
  deleteAdminJobPosting,
  deleteAdminPost,
  deleteAdminApplication,
  getAdminApplications,
  getAdminApplicationResumeUrl,
  getAdminCandidates,
  getAdminDbCollections,
  getAdminDbDocuments,
  getAdminDbInfo,
  getAdminJobPostings,
  getAdminJobs,
  getAdminPosts,
  getAdminStats,
  getAdminUsers,
  updateAdminApplication,
  updateAdminJob,
  updateAdminJobPosting,
  updateAdminPost,
  updateAdminUser,
  updateCandidateLevel,
  uploadAdminJobLogo,
  uploadAdminJobJd,
} from './adminApi';
import './AdminDashboard.css';
import {
  SALARY_CURRENCIES,
  SALARY_REGIONS,
  COMPENSATION_ARRANGEMENTS,
  emptyCompensationByArrangement,
  compensationToForm,
  parseCompensationForm,
  salaryPeriodFromJobType,
} from '../../config/salary';
import { JOB_DEPARTMENTS } from '../../config/jobDepartments';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'users', label: 'Users' },
  { id: 'candidates', label: 'Candidates' },
  { id: 'applications', label: 'Applications' },
  { id: 'feed', label: 'Feed' },
  { id: 'jobs', label: 'Jobs' },
];

const APPLICATION_STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'rejected', label: 'Rejected' },
];

const emptyJobForm = {
  id: '',
  title: '',
  department: 'Engineering',
  location: 'Remote',
  type: 'Full-time',
  salaryFrom: '',
  salaryTo: '',
  salaryCurrency: 'USD',
  salaryStructure: 'annual',
  regionalSalaries: emptyCompensationByArrangement(),
  compensationByArrangement: emptyCompensationByArrangement(),
  description: '',
  skills: [],
  skillDraft: '',
  isActive: true,
  startDate: '',
  deadlineDate: '',
  paymentStatus: '',
  plan: 'free',
  approvalStatus: 'approved',
  companyLogoUrl: '',
  jdFileUrl: '',
  jdFileName: '',
};

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
const MAX_JOB_SKILLS = 10;

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
};

const initialLetter = (value) => {
  const text = String(value || '').trim();
  return text ? text.charAt(0).toUpperCase() : '?';
};

const AdminMediaThumb = ({ src, label, alt = '' }) => {
  const resolved = resolveMediaUrl(src);
  return (
    <div className="admin-media-thumb" title={label || alt || ''}>
      {resolved ? (
        <img src={resolved} alt={alt || label || ''} />
      ) : (
        <span>{initialLetter(label || alt)}</span>
      )}
    </div>
  );
};

const formatDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const formatDisplayDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const previewDescription = (text, max = 140) => {
  const clean = String(text || '')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!clean) return 'No description provided';
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
};

const displayJobRef = (id) => {
  if (!id) return 'Draft';
  const value = String(id);
  if (value.length > 56) return 'Company listing';
  return value;
};

function AdminDashboard() {
  const navigate = useNavigate();
  const signout = useAdminAuthStore((state) => state.signout);
  const user = useAdminAuthStore((state) => state.user);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [userEmailTab, setUserEmailTab] = useState('personal');
  const [dbConnection, setDbConnection] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [applications, setApplications] = useState([]);
  const [applicationsTotal, setApplicationsTotal] = useState(0);
  const [applicationSearch, setApplicationSearch] = useState('');
  const [applicationStatus, setApplicationStatus] = useState('');
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [posts, setPosts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [candidateSearch, setCandidateSearch] = useState('');
  const [postStatus, setPostStatus] = useState('');
  const [jobForm, setJobForm] = useState(emptyJobForm);
  const [editingJobId, setEditingJobId] = useState(null);
  const [uploadingJobLogo, setUploadingJobLogo] = useState(false);
  const [uploadingJobJd, setUploadingJobJd] = useState(false);
  const [showAllPublishedJobs, setShowAllPublishedJobs] = useState(false);
  const INITIAL_PUBLISHED_JOBS = 3;
  const jobFormRef = useRef(null);
  const jobLogoInputRef = useRef(null);
  const jobJdInputRef = useRef(null);
  const [dbInfo, setDbInfo] = useState(null);
  const [dbCollections, setDbCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [dbDocuments, setDbDocuments] = useState([]);
  const [dbTotal, setDbTotal] = useState(0);
  const [dbSearch, setDbSearch] = useState('');
  const [dbOffset, setDbOffset] = useState(0);
  const dbLimit = 25;

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const loadOverview = useCallback(async () => {
    const data = await getAdminStats();
    setStats(data);
    if (data.database) setDbConnection(data.database);
  }, []);

  const loadUsers = useCallback(async () => {
    const params = { search: userSearch, limit: 100, emailType: userEmailTab };
    if (verifiedFilter) params.verified = verifiedFilter;
    const data = await getAdminUsers(params);
    setUsers(data.users || []);
    setUsersTotal(data.total || 0);
  }, [userSearch, verifiedFilter, userEmailTab]);

  const loadCandidates = useCallback(async () => {
    const data = await getAdminCandidates({ search: candidateSearch, limit: 50 });
    setCandidates(data.candidates || []);
  }, [candidateSearch]);

  const loadApplications = useCallback(async () => {
    const params = { search: applicationSearch, limit: 50 };
    if (applicationStatus) params.status = applicationStatus;
    const data = await getAdminApplications(params);
    setApplications(data.applications || []);
    setApplicationsTotal(data.total || 0);
  }, [applicationSearch, applicationStatus]);

  const loadPosts = useCallback(async () => {
    const params = { limit: 50 };
    if (postStatus) params.status = postStatus;
    const data = await getAdminPosts(params);
    setPosts(data.posts || []);
  }, [postStatus]);

  const loadJobs = useCallback(async () => {
    const [jobsData, postingsData] = await Promise.all([getAdminJobs(), getAdminJobPostings()]);
    setJobs(jobsData.jobs || []);
    setJobPostings(postingsData.jobPostings || []);
  }, []);

  const loadDatabase = useCallback(async () => {
    const [info, collectionsData] = await Promise.all([
      getAdminDbInfo(),
      getAdminDbCollections(),
    ]);
    setDbInfo(info);
    setDbConnection({
      name: info.database,
      host: info.host,
      provider: info.provider,
      isAtlas: info.isAtlas,
      readyState: info.readyState,
    });
    const collections = collectionsData.collections || [];
    setDbCollections(collections);
    setSelectedCollection((current) => current || collections[0]?.name || '');
  }, []);

  const loadDbDocuments = useCallback(async () => {
    if (!selectedCollection) {
      setDbDocuments([]);
      setDbTotal(0);
      return;
    }
    const data = await getAdminDbDocuments(selectedCollection, {
      limit: dbLimit,
      offset: dbOffset,
      search: dbSearch,
    });
    setDbDocuments(data.documents || []);
    setDbTotal(data.total || 0);
  }, [selectedCollection, dbOffset, dbSearch]);

  const loadTabData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'overview') await loadOverview();
      if (tab === 'users') await loadUsers();
      if (tab === 'candidates') await loadCandidates();
      if (tab === 'applications') await loadApplications();
      if (tab === 'feed') await loadPosts();
      if (tab === 'jobs') await loadJobs();
    } catch (error) {
      showMessage('error', error.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [tab, loadOverview, loadUsers, loadCandidates, loadApplications, loadPosts, loadJobs]);

  useEffect(() => {
    // Always load connection banner info on mount
    getAdminStats()
      .then((data) => {
        if (data.database) setDbConnection(data.database);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  // Database explorer tab removed from UI; keep loadDbDocuments available if re-enabled later.
  useEffect(() => {
    if (tab !== 'database') return;
    loadDbDocuments().catch((error) => {
      showMessage('error', error.message || 'Failed to load documents');
    });
  }, [tab, loadDbDocuments]);

  const handleUserUpdate = async (id, patch) => {
    try {
      await updateAdminUser(id, patch);
      showMessage('success', 'User updated');
      await loadUsers();
      if (tab === 'overview') await loadOverview();
    } catch (error) {
      showMessage('error', error.message || 'Failed to update user');
    }
  };

  const handleCandidateLevel = async (slug, level) => {
    try {
      await updateCandidateLevel(slug, Number(level));
      showMessage('success', 'Candidate level updated');
      await loadCandidates();
    } catch (error) {
      showMessage('error', error.message || 'Failed to update candidate');
    }
  };

  const handleApplicationStatus = async (id, status) => {
    try {
      await updateAdminApplication(id, { status });
      showMessage('success', `Application marked ${status}`);
      await loadApplications();
      if (tab === 'overview') await loadOverview();
    } catch (error) {
      showMessage('error', error.message || 'Failed to update application');
    }
  };

  const handleDeleteApplication = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await deleteAdminApplication(id);
      if (selectedApplicationId === id) setSelectedApplicationId(null);
      showMessage('success', 'Application deleted');
      await loadApplications();
    } catch (error) {
      showMessage('error', error.message || 'Failed to delete application');
    }
  };

  const handlePostStatus = async (id, status) => {
    try {
      await updateAdminPost(id, { status });
      showMessage('success', `Post marked ${status}`);
      await loadPosts();
      if (stats) await loadOverview();
    } catch (error) {
      showMessage('error', error.message || 'Failed to update post');
    }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Delete this post permanently?')) return;
    try {
      await deleteAdminPost(id);
      showMessage('success', 'Post deleted');
      await loadPosts();
    } catch (error) {
      showMessage('error', error.message || 'Failed to delete post');
    }
  };

  const startEditJob = (job) => {
    setEditingJobId(job.id || job._id);
    setJobForm({
      id: job.id || '',
      title: job.title || '',
      department: job.department || 'Engineering',
      location: job.location || '',
      type: job.type || 'Full-time',
      salaryFrom: job.salaryFrom ?? '',
      salaryTo: job.salaryTo ?? '',
      salaryCurrency: job.salaryCurrency || 'USD',
      salaryStructure: job.salaryStructure || salaryPeriodFromJobType(job.type),
      regionalSalaries: compensationToForm(job),
      compensationByArrangement: compensationToForm(job),
      description: job.description || '',
      skills: Array.isArray(job.skills) ? job.skills.slice(0, MAX_JOB_SKILLS) : [],
      skillDraft: '',
      isActive: job.isActive !== false,
      startDate: formatDateInput(job.startDate),
      deadlineDate: formatDateInput(job.deadlineDate),
      paymentStatus: job.paymentStatus || '',
      plan: job.plan === 'basic' || !job.plan ? 'free' : job.plan,
      approvalStatus: job.approvalStatus || 'approved',
      companyLogoUrl: job.companyLogoUrl || '',
      jdFileUrl: job.jdFileUrl || '',
      jdFileName: job.jdFileName || '',
    });
    window.setTimeout(() => {
      jobFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const resetJobForm = () => {
    setEditingJobId(null);
    setJobForm(emptyJobForm);
    if (jobLogoInputRef.current) jobLogoInputRef.current.value = '';
    if (jobJdInputRef.current) jobJdInputRef.current.value = '';
  };

  const handleJobLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingJobLogo(true);
    try {
      const data = await uploadAdminJobLogo(file);
      setJobForm((prev) => ({ ...prev, companyLogoUrl: data.logoUrl }));
      showMessage('success', 'Logo uploaded');
    } catch (error) {
      showMessage('error', error.message || 'Failed to upload logo');
    } finally {
      setUploadingJobLogo(false);
      if (jobLogoInputRef.current) jobLogoInputRef.current.value = '';
    }
  };

  const handleJobJdUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingJobJd(true);
    try {
      const data = await uploadAdminJobJd(file);
      setJobForm((prev) => ({
        ...prev,
        jdFileUrl: data.jdFileUrl || '',
        jdFileName: data.jdFileName || file.name,
      }));
      showMessage('success', 'JD file uploaded');
    } catch (error) {
      showMessage('error', error.message || 'Failed to upload JD file');
    } finally {
      setUploadingJobJd(false);
      if (jobJdInputRef.current) jobJdInputRef.current.value = '';
    }
  };

  const handleSaveJob = async (e) => {
    e.preventDefault();
    if (!jobForm.title.trim()) {
      showMessage('error', 'Title is required');
      return;
    }
    if (!String(jobForm.companyLogoUrl || '').trim()) {
      showMessage('error', 'Company logo image is required');
      return;
    }

    const compensationByArrangement = parseCompensationForm(
      jobForm.compensationByArrangement || jobForm.regionalSalaries
    );
    const usFrom = compensationByArrangement.fullTime?.usCanada?.from ?? null;
    const usTo = compensationByArrangement.fullTime?.usCanada?.to ?? null;
    const euFrom = compensationByArrangement.fullTime?.europe?.from ?? null;
    const euTo = compensationByArrangement.fullTime?.europe?.to ?? null;
    const salaryFrom = usFrom ?? euFrom;
    const salaryTo = usTo ?? euTo;
    const salaryStructure = salaryPeriodFromJobType(jobForm.type);

    for (const { id, label } of COMPENSATION_ARRANGEMENTS) {
      for (const { id: rid, label: rlabel } of SALARY_REGIONS) {
        const region = compensationByArrangement[id]?.[rid] || {};
        if (region.from !== null && region.to !== null && region.to < region.from) {
          showMessage('error', `${label} / ${rlabel}: salary "to" must be ≥ "from"`);
          return;
        }
      }
    }

    const payload = {
      title: jobForm.title,
      department: jobForm.department,
      location: jobForm.location,
      type: jobForm.type,
      salaryFrom,
      salaryTo,
      salaryCurrency: jobForm.salaryCurrency || 'USD',
      salaryStructure,
      regionalSalaries: compensationByArrangement.fullTime,
      compensationByArrangement,
      description: jobForm.description,
      responsibilities: [],
      requirements: [],
      benefits: [],
      skills: Array.isArray(jobForm.skills)
        ? jobForm.skills.map((s) => String(s || '').trim()).filter(Boolean).slice(0, MAX_JOB_SKILLS)
        : [],
      isActive: Boolean(jobForm.isActive),
      startDate: jobForm.startDate || null,
      deadlineDate: jobForm.deadlineDate || null,
      paymentStatus: jobForm.paymentStatus || '',
      plan: jobForm.plan === 'free' ? 'basic' : (jobForm.plan || 'basic'),
      approvalStatus: jobForm.approvalStatus || 'approved',
      companyLogoUrl: jobForm.companyLogoUrl,
      jdFileUrl: jobForm.jdFileUrl || '',
      jdFileName: jobForm.jdFileName || '',
    };

    if (!editingJobId && jobForm.id) {
      payload.id = jobForm.id;
    }

    try {
      if (editingJobId) {
        await updateAdminJob(editingJobId, payload);
        showMessage('success', 'Job updated');
      } else {
        await createAdminJob(payload);
        showMessage('success', 'Job created');
      }
      resetJobForm();
      await loadJobs();
    } catch (error) {
      showMessage('error', error.message || 'Failed to save job');
    }
  };

  const handleDeleteJob = async (id) => {
    if (!window.confirm('Delete this job listing?')) return;
    try {
      await deleteAdminJob(id);
      showMessage('success', 'Job deleted');
      if (editingJobId === id) resetJobForm();
      await loadJobs();
    } catch (error) {
      showMessage('error', error.message || 'Failed to delete job');
    }
  };

  const handleExpireJobPosting = async (id) => {
    try {
      await updateAdminJobPosting(id, { isExpired: true });
      showMessage('success', 'Job posting marked expired');
      await loadJobs();
    } catch (error) {
      showMessage('error', error.message || 'Failed to expire posting');
    }
  };

  const handleApproveJobPosting = async (id) => {
    try {
      await updateAdminJobPosting(id, { approvalStatus: 'approved' });
      showMessage('success', 'Job approved — employer notified');
      await loadJobs();
    } catch (error) {
      showMessage('error', error.message || 'Failed to approve posting');
    }
  };

  const handleRejectJobPosting = async (id) => {
    const feedback = window.prompt(
      'Optional feedback for the employer (what needs to change):',
      ''
    );
    if (feedback === null) return;
    try {
      await updateAdminJobPosting(id, {
        approvalStatus: 'rejected',
        adminFeedback: feedback.trim(),
      });
      showMessage('success', 'Job rejected — employer notified');
      await loadJobs();
    } catch (error) {
      showMessage('error', error.message || 'Failed to reject posting');
    }
  };

  const handleJobPostingPaymentStatus = async (id, paymentStatus) => {
    try {
      await updateAdminJobPosting(id, { paymentStatus });
      showMessage('success', `Payment status set to ${paymentStatus}`);
      await loadJobs();
    } catch (error) {
      showMessage('error', error.message || 'Failed to update payment status');
    }
  };

  const handleDeleteJobPosting = async (id) => {
    if (!window.confirm('Delete this company job posting and its published listing?')) return;
    try {
      await deleteAdminJobPosting(id);
      showMessage('success', 'Job posting deleted');
      await loadJobs();
    } catch (error) {
      showMessage('error', error.message || 'Failed to delete posting');
    }
  };

  const handleSignOut = () => {
    signout();
    navigate('/admin-signin');
  };

  return (
    <div className="admin-page">
      <section className="admin-hero">
        <div className="admin-hero-inner">
          <div>
            <h1>CofounderHubs Admin</h1>
            <p>Manage users, candidates, job applications, feed moderation, and jobs</p>
          </div>
          <div className="admin-hero-actions">
            {user?.email && <span className="admin-user-email">{user.email}</span>}
            <button type="button" className="admin-btn secondary" onClick={handleSignOut}>
              Sign out
            </button>
          </div>
        </div>
      </section>

      <div className="admin-container">
        <div className="admin-tabs">
          {TABS.map((item) => (
            <button
              key={item.id}
              className={`admin-tab ${tab === item.id ? 'active' : ''}`}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {message && (
          <div className={`admin-message ${message.type}`}>{message.text}</div>
        )}

        {dbConnection && (
          <div className={`admin-db-banner ${dbConnection.isAtlas ? 'atlas' : 'local'}`}>
            <strong>{dbConnection.provider || 'MongoDB'}</strong>
            <span>
              {dbConnection.name || 'unknown'} @ {dbConnection.host || 'unknown'}
            </span>
            {!dbConnection.isAtlas && (
              <span className="admin-db-banner-note">
                Connected to local MongoDB — accounts created on production/Atlas will not appear here until you set an Atlas `MONGODB_URI`.
              </span>
            )}
          </div>
        )}

        {loading ? (
          <div className="admin-loading">Loading admin data...</div>
        ) : (
          <>
            {tab === 'overview' && stats && (
              <>
                <div className="admin-stats-grid">
                  {Object.entries(stats.stats || {}).map(([key, value]) => (
                    <div key={key} className="admin-stat-card">
                      <span>{key}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
                <h3>Recent users</h3>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Level</th>
                        <th>Role</th>
                        <th>Verified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(stats.recentUsers || []).map((user) => (
                        <tr key={user._id || user.email}>
                          <td>
                            <div className="admin-entity-cell">
                              <AdminMediaThumb
                                src={user.companyLogoUrl || user.avatarUrl}
                                label={user.companyName || user.fullName || user.username}
                              />
                              <div>
                                <div>{user.username}</div>
                                {user.companyName ? (
                                  <div className="admin-muted">{user.companyName}</div>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td>{user.level}</td>
                          <td>{user.role}</td>
                          <td>{user.isVerified ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {tab === 'database' && (
              <>
                {dbInfo && (
                  <div className="admin-stats-grid">
                    <div className="admin-stat-card">
                      <span>database</span>
                      <strong style={{ fontSize: '1rem' }}>{dbInfo.database}</strong>
                    </div>
                    <div className="admin-stat-card">
                      <span>host</span>
                      <strong style={{ fontSize: '1rem' }}>{dbInfo.host || 'local'}</strong>
                    </div>
                    <div className="admin-stat-card">
                      <span>collections</span>
                      <strong>{dbInfo.collections ?? dbCollections.length}</strong>
                    </div>
                    <div className="admin-stat-card">
                      <span>objects</span>
                      <strong>{dbInfo.objects ?? '—'}</strong>
                    </div>
                    <div className="admin-stat-card">
                      <span>dataSize</span>
                      <strong>{dbInfo.dataSize != null ? `${Math.round(dbInfo.dataSize / 1024)} KB` : '—'}</strong>
                    </div>
                    <div className="admin-stat-card">
                      <span>version</span>
                      <strong style={{ fontSize: '1rem' }}>{dbInfo.version || '—'}</strong>
                    </div>
                  </div>
                )}

                <div className="admin-toolbar">
                  <select
                    value={selectedCollection}
                    onChange={(e) => {
                      setSelectedCollection(e.target.value);
                      setDbOffset(0);
                    }}
                  >
                    {dbCollections.map((collection) => (
                      <option key={collection.name} value={collection.name}>
                        {collection.name} ({collection.count})
                      </option>
                    ))}
                  </select>
                  <input
                    value={dbSearch}
                    onChange={(e) => setDbSearch(e.target.value)}
                    placeholder="Search by email, username, name, slug, title, or _id"
                  />
                  <button
                    className="admin-btn"
                    onClick={() => {
                      setDbOffset(0);
                      loadDbDocuments().catch((error) => showMessage('error', error.message));
                    }}
                  >
                    Search
                  </button>
                </div>

                <p className="admin-muted">
                  Showing {dbDocuments.length} of {dbTotal} documents in <strong>{selectedCollection || '—'}</strong>
                  . Sensitive fields are redacted.
                </p>

                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th style={{ width: '220px' }}>_id</th>
                        <th>Document</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dbDocuments.map((doc) => (
                        <tr key={String(doc._id)}>
                          <td className="admin-muted">{String(doc._id)}</td>
                          <td>
                            <pre className="admin-json">{JSON.stringify(doc, null, 2)}</pre>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {dbDocuments.length === 0 && <div className="admin-empty">No documents found</div>}
                </div>

                <div className="admin-actions" style={{ marginTop: '1rem' }}>
                  <button
                    className="admin-btn secondary"
                    disabled={dbOffset <= 0}
                    onClick={() => setDbOffset((prev) => Math.max(0, prev - dbLimit))}
                  >
                    Previous
                  </button>
                  <button
                    className="admin-btn secondary"
                    disabled={dbOffset + dbLimit >= dbTotal}
                    onClick={() => setDbOffset((prev) => prev + dbLimit)}
                  >
                    Next
                  </button>
                </div>
              </>
            )}

            {tab === 'users' && (
              <>
                <p className="admin-muted" style={{ marginBottom: '1rem' }}>
                  Signup accounts appear here (Users). The Candidates tab is the cofounder directory, not signup accounts.
                </p>
                <div className="admin-subtabs">
                  <button
                    type="button"
                    className={`admin-subtab ${userEmailTab === 'personal' ? 'active' : ''}`}
                    onClick={() => setUserEmailTab('personal')}
                  >
                    Personal email
                  </button>
                  <button
                    type="button"
                    className={`admin-subtab ${userEmailTab === 'company' ? 'active' : ''}`}
                    onClick={() => setUserEmailTab('company')}
                  >
                    Company email
                  </button>
                </div>
                <div className="admin-toolbar">
                  <input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search users by name, username, or email"
                  />
                  <select
                    value={verifiedFilter}
                    onChange={(e) => setVerifiedFilter(e.target.value)}
                  >
                    <option value="">All verification</option>
                    <option value="true">Verified only</option>
                    <option value="false">Unverified only</option>
                  </select>
                  <button className="admin-btn" onClick={loadUsers}>Search</button>
                </div>
                <p className="admin-muted">
                  Showing {users.length} of {usersTotal}{' '}
                  {userEmailTab === 'company' ? 'company email' : 'personal email'} users
                </p>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Provider</th>
                        <th>Created</th>
                        <th>Level</th>
                        <th>Role</th>
                        <th>Verified</th>
                        <th>Blocked</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="admin-entity-cell">
                              <AdminMediaThumb
                                src={
                                  userEmailTab === 'company'
                                    ? user.companyLogoUrl || user.avatarUrl
                                    : user.avatarUrl || user.companyLogoUrl
                                }
                                label={user.companyName || user.fullName || user.username}
                              />
                              <div>
                                <div>{user.fullName || user.username}</div>
                                <div className="admin-muted">{user.email}</div>
                                <div className="admin-muted">
                                  @{user.username}
                                  {user.accountType === 'employer' ? ' · employer' : ''}
                                  {user.companyName ? ` · ${user.companyName}` : ''}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>{user.authProvider || (user.googleId ? 'google' : 'email')}</td>
                          <td className="admin-muted">
                            {user.createdAt ? new Date(user.createdAt).toLocaleString() : '—'}
                          </td>
                          <td>
                            <input
                              className="admin-inline-input"
                              type="number"
                              defaultValue={user.level}
                              onBlur={(e) => {
                                const next = Number(e.target.value);
                                if (next !== user.level) handleUserUpdate(user.id, { level: next });
                              }}
                            />
                          </td>
                          <td>
                            <select
                              defaultValue={user.role}
                              onChange={(e) => handleUserUpdate(user.id, { role: e.target.value })}
                            >
                              <option value="user">user</option>
                              <option value="admin">admin</option>
                            </select>
                          </td>
                          <td>{user.isVerified ? 'Yes' : 'No'}</td>
                          <td>{user.blocked ? 'Yes' : 'No'}</td>
                          <td className="admin-actions">
                            {!user.isVerified && (
                              <button
                                className="admin-btn secondary"
                                onClick={() => handleUserUpdate(user.id, { isVerified: true })}
                              >
                                Verify
                              </button>
                            )}
                            <button
                              className={`admin-btn ${user.blocked ? 'secondary' : 'danger'}`}
                              onClick={() =>
                                handleUserUpdate(user.id, { blocked: !user.blocked })
                              }
                            >
                              {user.blocked ? 'Unblock' : 'Block'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && (
                    <div className="admin-empty">
                      No users found in this database.
                      {!dbConnection?.isAtlas && (
                        <> If you signed up on the live site, that account is in MongoDB Atlas — paste your Atlas connection string into backend `.env` as `MONGODB_URI` and restart the server.</>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {tab === 'candidates' && (
              <>
                <div className="admin-toolbar">
                  <input
                    value={candidateSearch}
                    onChange={(e) => setCandidateSearch(e.target.value)}
                    placeholder="Search candidates by name, slug, or location"
                  />
                  <button className="admin-btn" onClick={loadCandidates}>Search</button>
                </div>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Candidate</th>
                        <th>Location</th>
                        <th>Level</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map((candidate) => (
                        <tr key={candidate.slug}>
                          <td>
                            <div className="admin-entity-cell">
                              <AdminMediaThumb
                                src={candidate.avatarUrl}
                                label={candidate.name || candidate.slug}
                              />
                              <div>
                                <div>{candidate.name}</div>
                                <div className="admin-muted">{candidate.slug}</div>
                              </div>
                            </div>
                          </td>
                          <td>{candidate.location || candidate.country || '—'}</td>
                          <td>
                            <input
                              className="admin-inline-input"
                              type="number"
                              defaultValue={candidate.level}
                              id={`level-${candidate.slug}`}
                            />
                          </td>
                          <td>
                            <button
                              className="admin-btn"
                              onClick={() => {
                                const input = document.getElementById(`level-${candidate.slug}`);
                                handleCandidateLevel(candidate.slug, input?.value);
                              }}
                            >
                              Save level
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {candidates.length === 0 && <div className="admin-empty">No candidates found</div>}
                </div>
              </>
            )}

            {tab === 'applications' && (
              <>
                <div className="admin-toolbar">
                  <input
                    value={applicationSearch}
                    onChange={(e) => setApplicationSearch(e.target.value)}
                    placeholder="Search by name, email, job, or company"
                  />
                  <select
                    value={applicationStatus}
                    onChange={(e) => setApplicationStatus(e.target.value)}
                  >
                    <option value="">All statuses</option>
                    {APPLICATION_STATUSES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <button className="admin-btn" onClick={loadApplications}>
                    Search
                  </button>
                  <button
                    type="button"
                    className="admin-btn secondary"
                    onClick={async () => {
                      try {
                        await loadApplications();
                        showMessage('success', 'Applications updated');
                      } catch (error) {
                        showMessage('error', error.message || 'Failed to refresh applications');
                      }
                    }}
                  >
                    Refresh
                  </button>
                  <span className="admin-muted">{applicationsTotal} total</span>
                </div>

                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Applicant</th>
                        <th>Job</th>
                        <th>Submitted</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((application) => {
                        const isOpen = selectedApplicationId === application.id;
                        const employment = application.employmentType || {};
                        const employmentLabels = [
                          employment.fullTime ? 'Full-time' : null,
                          employment.partTime ? 'Part-time' : null,
                          employment.contract ? 'Contract' : null,
                        ].filter(Boolean);
                        return (
                          <React.Fragment key={application.id}>
                            <tr className={isOpen ? 'admin-row-selected' : undefined}>
                              <td>
                                <div className="admin-entity-cell">
                                  <AdminMediaThumb label={application.fullName} />
                                  <div>
                                    <div>{application.fullName}</div>
                                    <div className="admin-muted">{application.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div>{application.jobTitle || application.jobId}</div>
                                <div className="admin-muted">
                                  {application.companyName || application.currentLocation || '—'}
                                </div>
                              </td>
                              <td>{formatDisplayDate(application.createdAt)}</td>
                              <td>
                                <span className={`admin-status-pill status-${application.status}`}>
                                  {application.status}
                                </span>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="admin-btn"
                                  onClick={() =>
                                    setSelectedApplicationId((current) =>
                                      current === application.id ? null : application.id
                                    )
                                  }
                                >
                                  {isOpen ? 'Hide' : 'View'}
                                </button>
                                <button
                                  type="button"
                                  className="admin-btn secondary"
                                  onClick={() => handleApplicationStatus(application.id, 'reviewed')}
                                >
                                  Reviewed
                                </button>
                                <button
                                  type="button"
                                  className="admin-btn danger"
                                  onClick={() => handleDeleteApplication(application.id)}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                            {isOpen ? (
                              <tr className="admin-application-dropdown-row">
                                <td colSpan={5}>
                                  <div className="admin-application-dropdown">
                                    <div className="admin-application-grid">
                                      <div>
                                        <div className="admin-muted">Email</div>
                                        <a href={`mailto:${application.email}`}>{application.email}</a>
                                      </div>
                                      <div>
                                        <div className="admin-muted">Phone</div>
                                        <div>{application.phone || '—'}</div>
                                      </div>
                                      <div>
                                        <div className="admin-muted">Location</div>
                                        <div>{application.currentLocation || '—'}</div>
                                      </div>
                                      <div>
                                        <div className="admin-muted">Experience</div>
                                        <div>{application.yearsOfExperience || '—'}</div>
                                      </div>
                                      <div>
                                        <div className="admin-muted">LinkedIn</div>
                                        {application.linkedinUrl ? (
                                          <a href={application.linkedinUrl} target="_blank" rel="noreferrer">
                                            Profile
                                          </a>
                                        ) : (
                                          '—'
                                        )}
                                      </div>
                                      <div>
                                        <div className="admin-muted">GitHub</div>
                                        {application.githubUrl ? (
                                          <a href={application.githubUrl} target="_blank" rel="noreferrer">
                                            Profile
                                          </a>
                                        ) : (
                                          '—'
                                        )}
                                      </div>
                                      <div>
                                        <div className="admin-muted">Employment</div>
                                        <div>
                                          {employmentLabels.length ? employmentLabels.join(', ') : '—'}
                                        </div>
                                      </div>
                                      <div>
                                        <div className="admin-muted">Rate</div>
                                        <div>
                                          {application.rateType === 'monthly'
                                            ? `Monthly: ${application.monthlyRate || '—'}`
                                            : `Hourly: ${application.hourlyRate || '—'}`}
                                        </div>
                                      </div>
                                      <div>
                                        <div className="admin-muted">Submitted</div>
                                        <div>{formatDisplayDate(application.createdAt)}</div>
                                      </div>
                                      <div>
                                        <div className="admin-muted">Resume</div>
                                        {application.resumeUrl ? (
                                          <a
                                            href={getAdminApplicationResumeUrl(application.id, {
                                              download: true,
                                            })}
                                            download={application.resumeFileName || 'resume'}
                                          >
                                            {application.resumeFileName || 'Download resume'}
                                          </a>
                                        ) : (
                                          '—'
                                        )}
                                      </div>
                                    </div>
                                    {application.coverLetter ? (
                                      <div className="admin-application-cover">
                                        <div className="admin-muted">Cover letter</div>
                                        <p>{application.coverLetter}</p>
                                      </div>
                                    ) : null}
                                    <div className="admin-application-actions">
                                      {APPLICATION_STATUSES.map((item) => (
                                        <button
                                          key={item.value}
                                          type="button"
                                          className={`admin-btn ${
                                            application.status === item.value ? '' : 'secondary'
                                          }`}
                                          onClick={() =>
                                            handleApplicationStatus(application.id, item.value)
                                          }
                                        >
                                          {item.label}
                                        </button>
                                      ))}
                                      <button
                                        type="button"
                                        className="admin-btn danger"
                                        onClick={() => handleDeleteApplication(application.id)}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  {applications.length === 0 && (
                    <div className="admin-empty">No applications found</div>
                  )}
                </div>
              </>
            )}

            {tab === 'feed' && (
              <>
                <div className="admin-toolbar">
                  <select value={postStatus} onChange={(e) => setPostStatus(e.target.value)}>
                    <option value="">All statuses</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <button className="admin-btn" onClick={loadPosts}>Refresh</button>
                </div>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Post</th>
                        <th>Author</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {posts.map((post) => (
                        <tr key={post.id}>
                          <td>
                            <div className="admin-entity-cell">
                              <AdminMediaThumb
                                src={post.imageUrl || post.user?.avatarUrl}
                                label={post.title || post.user?.username || 'Post'}
                              />
                              <div>
                                <div>{post.title || 'Untitled'}</div>
                                <div className="admin-muted">
                                  {(post.content || '').slice(0, 120)}
                                  {(post.content || '').length > 120 ? '…' : ''}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="admin-entity-cell">
                              <AdminMediaThumb
                                src={post.user?.avatarUrl}
                                label={post.user?.fullName || post.user?.username || 'User'}
                              />
                              <span>{post.user?.username || '—'}</span>
                            </div>
                          </td>
                          <td>{post.status}</td>
                          <td className="admin-actions">
                            <button className="admin-btn secondary" onClick={() => handlePostStatus(post.id, 'approved')}>
                              Approve
                            </button>
                            <button className="admin-btn secondary" onClick={() => handlePostStatus(post.id, 'rejected')}>
                              Reject
                            </button>
                            <button className="admin-btn danger" onClick={() => handleDeletePost(post.id)}>
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {posts.length === 0 && <div className="admin-empty">No posts found</div>}
                </div>
              </>
            )}

            {tab === 'jobs' && (
              <>
                <form className="admin-form" onSubmit={handleSaveJob} ref={jobFormRef}>
                  <h3>{editingJobId ? `Edit job: ${jobForm.title || editingJobId}` : 'Create job'}</h3>
                  <div className="admin-form-row" style={{ alignItems: 'center' }}>
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                        overflow: 'hidden',
                        background: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {jobForm.companyLogoUrl ? (
                        <img
                          src={resolveMediaUrl(jobForm.companyLogoUrl)}
                          alt="Company logo"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <span className="admin-muted">Logo</span>
                      )}
                    </div>
                    <div>
                      <div className="admin-muted">Company logo *</div>
                      <input
                        ref={jobLogoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        hidden
                        onChange={handleJobLogoUpload}
                      />
                      <button
                        type="button"
                        className="admin-btn secondary"
                        disabled={uploadingJobLogo}
                        onClick={() => jobLogoInputRef.current?.click()}
                      >
                        {uploadingJobLogo
                          ? 'Uploading...'
                          : jobForm.companyLogoUrl
                            ? 'Change logo'
                            : 'Upload logo'}
                      </button>
                    </div>
                  </div>
                  <div className="admin-form-row" style={{ alignItems: 'center' }}>
                    <div>
                      <div className="admin-muted">JD file (optional — PDF, DOC, DOCX)</div>
                      <input
                        ref={jobJdInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        hidden
                        onChange={handleJobJdUpload}
                      />
                      <button
                        type="button"
                        className="admin-btn secondary"
                        disabled={uploadingJobJd}
                        onClick={() => jobJdInputRef.current?.click()}
                      >
                        {uploadingJobJd
                          ? 'Uploading...'
                          : jobForm.jdFileUrl
                            ? 'Replace JD file'
                            : 'Upload JD file'}
                      </button>
                      {jobForm.jdFileUrl ? (
                        <span className="admin-muted" style={{ marginLeft: 10 }}>
                          {jobForm.jdFileName || 'JD uploaded'}{' '}
                          <a
                            href={resolveMediaUrl(jobForm.jdFileUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Preview
                          </a>
                          {' · '}
                          <button
                            type="button"
                            className="admin-btn secondary"
                            style={{ padding: '2px 8px' }}
                            onClick={() =>
                              setJobForm((prev) => ({ ...prev, jdFileUrl: '', jdFileName: '' }))
                            }
                          >
                            Remove
                          </button>
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="admin-form-row">
                    <input
                      placeholder="Job id (optional, auto from title)"
                      value={jobForm.id}
                      disabled={Boolean(editingJobId)}
                      onChange={(e) => setJobForm({ ...jobForm, id: e.target.value })}
                    />
                    <input
                      placeholder="Title *"
                      required
                      value={jobForm.title}
                      onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                    />
                    <select
                      value={
                        JOB_DEPARTMENTS.includes(jobForm.department)
                          ? jobForm.department
                          : jobForm.department || 'Engineering'
                      }
                      onChange={(e) => setJobForm({ ...jobForm, department: e.target.value })}
                      aria-label="Department"
                    >
                      {!JOB_DEPARTMENTS.includes(jobForm.department) && jobForm.department ? (
                        <option value={jobForm.department}>{jobForm.department}</option>
                      ) : null}
                      {JOB_DEPARTMENTS.map((dept) => (
                        <option key={dept} value={dept}>
                          {dept}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="admin-form-row">
                    <input
                      placeholder="Location"
                      value={jobForm.location}
                      onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })}
                    />
                    <select
                      value={jobForm.type}
                      onChange={(e) => setJobForm({ ...jobForm, type: e.target.value })}
                    >
                      <option>Full-time</option>
                      <option>Part-time</option>
                      <option>Contract</option>
                      <option>Internship</option>
                    </select>
                    <label className="admin-muted">
                      <input
                        type="checkbox"
                        checked={jobForm.isActive}
                        onChange={(e) => setJobForm({ ...jobForm, isActive: e.target.checked })}
                      />{' '}
                      Active on jobs page
                    </label>
                  </div>
                  <div className="admin-form-row">
                    <select
                      value={jobForm.salaryCurrency}
                      onChange={(e) => setJobForm({ ...jobForm, salaryCurrency: e.target.value })}
                      aria-label="Salary currency"
                    >
                      {SALARY_CURRENCIES.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </select>
                    <span className="admin-muted" style={{ alignSelf: 'center' }}>
                      Structure:{' '}
                      <strong>
                        {salaryPeriodFromJobType(jobForm.type) === 'monthly'
                          ? 'Monthly (part-time / contract)'
                          : 'Annual (full-time)'}
                      </strong>
                    </span>
                  </div>
                  <div className="admin-muted" style={{ marginBottom: 6 }}>
                    Compensation by arrangement (Full Time = annual; others = monthly). Regions: US &amp;
                    Canada (higher), Europe, Other (lower). No seniority levels.
                  </div>
                  {COMPENSATION_ARRANGEMENTS.map(({ id, label, period }) => (
                    <div key={id} style={{ marginBottom: 12 }}>
                      <div className="admin-muted" style={{ marginBottom: 4, fontWeight: 700 }}>
                        {label} · {period}
                      </div>
                      {SALARY_REGIONS.map(({ id: rid, label: rlabel }) => (
                        <div className="admin-form-row" key={`${id}-${rid}`}>
                          <span className="admin-muted" style={{ minWidth: 110, alignSelf: 'center' }}>
                            {rlabel}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            placeholder="From"
                            value={jobForm.compensationByArrangement?.[id]?.[rid]?.from ?? ''}
                            onChange={(e) =>
                              setJobForm({
                                ...jobForm,
                                compensationByArrangement: {
                                  ...jobForm.compensationByArrangement,
                                  [id]: {
                                    ...(jobForm.compensationByArrangement?.[id] || {}),
                                    [rid]: {
                                      ...(jobForm.compensationByArrangement?.[id]?.[rid] || {}),
                                      from: e.target.value,
                                    },
                                  },
                                },
                              })
                            }
                          />
                          <input
                            type="number"
                            min="0"
                            step="1"
                            placeholder="To"
                            value={jobForm.compensationByArrangement?.[id]?.[rid]?.to ?? ''}
                            onChange={(e) =>
                              setJobForm({
                                ...jobForm,
                                compensationByArrangement: {
                                  ...jobForm.compensationByArrangement,
                                  [id]: {
                                    ...(jobForm.compensationByArrangement?.[id] || {}),
                                    [rid]: {
                                      ...(jobForm.compensationByArrangement?.[id]?.[rid] || {}),
                                      to: e.target.value,
                                    },
                                  },
                                },
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                  <div className="admin-form-row">
                    <label className="admin-muted">
                      Start date
                      <input
                        type="date"
                        value={jobForm.startDate}
                        onChange={(e) => setJobForm({ ...jobForm, startDate: e.target.value })}
                      />
                    </label>
                    <label className="admin-muted">
                      Deadline date
                      <input
                        type="date"
                        value={jobForm.deadlineDate}
                        onChange={(e) => setJobForm({ ...jobForm, deadlineDate: e.target.value })}
                      />
                    </label>
                    <label className="admin-muted">
                      Plan
                      <select
                        value={jobForm.plan || 'free'}
                        onChange={(e) => setJobForm({ ...jobForm, plan: e.target.value })}
                      >
                        <option value="free">free</option>
                        <option value="enhanced">enhanced</option>
                        <option value="premium">premium</option>
                      </select>
                    </label>
                    <label className="admin-muted">
                      Approval
                      <select
                        value={jobForm.approvalStatus}
                        onChange={(e) => setJobForm({ ...jobForm, approvalStatus: e.target.value })}
                      >
                        <option value="approved">approved</option>
                        <option value="pending">pending</option>
                        <option value="rejected">rejected</option>
                      </select>
                    </label>
                  </div>
                  <textarea
                    placeholder="Job description"
                    rows={14}
                    value={jobForm.description}
                    onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                  />
                  <div className="admin-skills-block">
                    <div className="admin-muted" style={{ marginBottom: 6 }}>
                      Skills (max {MAX_JOB_SKILLS})
                    </div>
                    <div className="admin-form-row" style={{ marginBottom: 8 }}>
                      <input
                        placeholder="Add a skill (e.g. React, Python)"
                        value={jobForm.skillDraft || ''}
                        disabled={(jobForm.skills || []).length >= MAX_JOB_SKILLS}
                        onChange={(e) => setJobForm({ ...jobForm, skillDraft: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const next = String(jobForm.skillDraft || '').trim();
                            if (!next) return;
                            if ((jobForm.skills || []).length >= MAX_JOB_SKILLS) return;
                            if ((jobForm.skills || []).some((s) => s.toLowerCase() === next.toLowerCase())) {
                              setJobForm({ ...jobForm, skillDraft: '' });
                              return;
                            }
                            setJobForm({
                              ...jobForm,
                              skills: [...(jobForm.skills || []), next],
                              skillDraft: '',
                            });
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="admin-btn secondary"
                        disabled={(jobForm.skills || []).length >= MAX_JOB_SKILLS}
                        onClick={() => {
                          const next = String(jobForm.skillDraft || '').trim();
                          if (!next) return;
                          if ((jobForm.skills || []).length >= MAX_JOB_SKILLS) return;
                          if ((jobForm.skills || []).some((s) => s.toLowerCase() === next.toLowerCase())) {
                            setJobForm({ ...jobForm, skillDraft: '' });
                            return;
                          }
                          setJobForm({
                            ...jobForm,
                            skills: [...(jobForm.skills || []), next],
                            skillDraft: '',
                          });
                        }}
                      >
                        Add skill
                      </button>
                    </div>
                    <div className="admin-skills-list">
                      {(jobForm.skills || []).map((skill) => (
                        <span key={skill} className="admin-skill-chip">
                          {skill}
                          <button
                            type="button"
                            aria-label={`Remove ${skill}`}
                            onClick={() =>
                              setJobForm({
                                ...jobForm,
                                skills: (jobForm.skills || []).filter((s) => s !== skill),
                              })
                            }
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="admin-actions">
                    <button className="admin-btn" type="submit">
                      {editingJobId ? 'Update job' : 'Create job'}
                    </button>
                    {editingJobId && (
                      <button className="admin-btn secondary" type="button" onClick={resetJobForm}>
                        Cancel edit
                      </button>
                    )}
                  </div>
                </form>

                <div className="admin-table-wrap">
                  <h3>Pending review ({jobPostings.filter((p) => (p.approvalStatus || 'pending') === 'pending' && p.isPublished).length})</h3>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Job</th>
                        <th>Company</th>
                        <th>Plan / pay</th>
                        <th>Dates</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobPostings
                        .filter((p) => (p.approvalStatus || 'pending') === 'pending' && p.isPublished)
                        .map((posting) => (
                          <tr key={posting._id}>
                            <td>
                              <div className="admin-entity-cell">
                                <AdminMediaThumb
                                  src={posting.companyId?.logoUrl}
                                  label={posting.companyId?.name || posting.title}
                                />
                                <div>
                                  <div>{posting.title}</div>
                                  <div className="admin-muted">{previewDescription(posting.description)}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              {posting.companyId?.name || '—'}
                              <div className="admin-muted">{posting.userId?.email || ''}</div>
                            </td>
                            <td>
                              {posting.plan} ·{' '}
                              <select
                                value={posting.paymentStatus || 'draft'}
                                onChange={(e) =>
                                  handleJobPostingPaymentStatus(posting._id, e.target.value)
                                }
                              >
                                <option value="free">free</option>
                                <option value="pending">pending</option>
                                <option value="paid">paid</option>
                                <option value="failed">failed</option>
                                <option value="draft">draft</option>
                              </select>
                            </td>
                            <td>
                              {formatDisplayDate(posting.startDate)} → {formatDisplayDate(posting.deadlineDate)}
                            </td>
                            <td className="admin-actions">
                              <button
                                className="admin-btn secondary"
                                onClick={() => handleApproveJobPosting(posting._id)}
                              >
                                Approve
                              </button>
                              <button
                                className="admin-btn secondary"
                                onClick={() => handleRejectJobPosting(posting._id)}
                              >
                                Reject
                              </button>
                              <button
                                className="admin-btn danger"
                                onClick={() => handleDeleteJobPosting(posting._id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {jobPostings.filter((p) => (p.approvalStatus || 'pending') === 'pending' && p.isPublished).length === 0 && (
                    <div className="admin-empty">No pending company job postings</div>
                  )}
                </div>

                <div className="admin-table-wrap" style={{ marginTop: 24 }}>
                  <h3>
                    Published jobs (
                    {jobs.filter((j) => (j.approvalStatus || 'approved') === 'approved').length +
                      jobPostings.filter(
                        (p) =>
                          p.approvalStatus === 'approved' &&
                          !jobs.some((j) => j.id === p.publishedJobId)
                      ).length}
                    )
                  </h3>
                  {(() => {
                    const publishedJobs = jobs.filter(
                      (j) => (j.approvalStatus || 'approved') === 'approved'
                    );
                    const orphanPostings = jobPostings.filter(
                      (p) =>
                        p.approvalStatus === 'approved' &&
                        !jobs.some((j) => j.id === p.publishedJobId)
                    );
                    const totalPublished = publishedJobs.length + orphanPostings.length;
                    const visibleJobs = showAllPublishedJobs
                      ? publishedJobs
                      : publishedJobs.slice(0, INITIAL_PUBLISHED_JOBS);
                    const remainingSlots = showAllPublishedJobs
                      ? orphanPostings.length
                      : Math.max(0, INITIAL_PUBLISHED_JOBS - visibleJobs.length);
                    const visiblePostings = showAllPublishedJobs
                      ? orphanPostings
                      : orphanPostings.slice(0, remainingSlots);

                    return (
                      <>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Job</th>
                        <th>Meta</th>
                        <th>Dates</th>
                        <th>Pay</th>
                        <th>Source</th>
                        <th>Active</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleJobs.map((job) => (
                          <tr key={`job-${job.id}`}>
                            <td>
                              <div className="admin-entity-cell">
                                <AdminMediaThumb
                                  src={job.companyLogoUrl}
                                  label={job.title}
                                />
                                <div>
                                  <div>{job.title}</div>
                                  <div className="admin-muted">{previewDescription(job.description)}</div>
                                  <div className="admin-muted">{displayJobRef(job.id)}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              {job.department} · {job.location} · {job.type}
                              {job.salary ? ` · ${job.salary}` : ''}
                              {job.plan ? ` · ${job.plan}` : ''}
                            </td>
                            <td>
                              {formatDisplayDate(job.startDate)} → {formatDisplayDate(job.deadlineDate)}
                            </td>
                            <td>{job.paymentStatus || '—'}</td>
                            <td>{job.source === 'company' ? 'Company' : 'Admin'}</td>
                            <td>{job.isActive ? 'Yes' : 'No'}</td>
                            <td className="admin-actions">
                              <button className="admin-btn secondary" onClick={() => startEditJob(job)}>
                                Edit
                              </button>
                              <button className="admin-btn danger" onClick={() => handleDeleteJob(job.id)}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      {visiblePostings.map((posting) => (
                          <tr key={`posting-pub-${posting._id}`}>
                            <td>
                              <div className="admin-entity-cell">
                                <AdminMediaThumb
                                  src={posting.companyId?.logoUrl}
                                  label={posting.companyId?.name || posting.title}
                                />
                                <div>
                                  <div>{posting.title}</div>
                                  <div className="admin-muted">{previewDescription(posting.description)}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              {posting.plan} · {posting.companyId?.name || 'Company'}
                            </td>
                            <td>
                              {formatDisplayDate(posting.startDate)} → {formatDisplayDate(posting.deadlineDate)}
                            </td>
                            <td>{posting.paymentStatus || '—'}</td>
                            <td>Company</td>
                            <td>Yes</td>
                            <td className="admin-actions">
                              <button
                                className="admin-btn secondary"
                                onClick={() => handleRejectJobPosting(posting._id)}
                              >
                                Reject
                              </button>
                              <button
                                className="admin-btn danger"
                                onClick={() => handleDeleteJobPosting(posting._id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {totalPublished === 0 && (
                      <div className="admin-empty">No published jobs yet</div>
                    )}
                  {totalPublished > INITIAL_PUBLISHED_JOBS ? (
                    <div className="admin-actions" style={{ marginTop: 12, justifyContent: 'center' }}>
                      <button
                        type="button"
                        className="admin-btn secondary"
                        onClick={() => setShowAllPublishedJobs((prev) => !prev)}
                      >
                        {showAllPublishedJobs
                          ? 'Show less'
                          : `View all (${totalPublished})`}
                      </button>
                    </div>
                  ) : null}
                      </>
                    );
                  })()}
                </div>

                <div className="admin-table-wrap" style={{ marginTop: 24 }}>
                  <h3>
                    Rejected jobs ({jobPostings.filter((p) => p.approvalStatus === 'rejected').length})
                  </h3>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Job</th>
                        <th>Company</th>
                        <th>Feedback</th>
                        <th>Dates</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobPostings
                        .filter((p) => p.approvalStatus === 'rejected')
                        .map((posting) => (
                          <tr key={posting._id}>
                            <td>
                              <div className="admin-entity-cell">
                                <AdminMediaThumb
                                  src={posting.companyId?.logoUrl}
                                  label={posting.companyId?.name || posting.title}
                                />
                                <div>
                                  <div>{posting.title}</div>
                                  <div className="admin-muted">{previewDescription(posting.description)}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              {posting.companyId?.name || '—'}
                              <div className="admin-muted">{posting.userId?.email || ''}</div>
                            </td>
                            <td>{posting.adminFeedback || '—'}</td>
                            <td>
                              {formatDisplayDate(posting.startDate)} → {formatDisplayDate(posting.deadlineDate)}
                            </td>
                            <td className="admin-actions">
                              <button
                                className="admin-btn secondary"
                                onClick={() => handleApproveJobPosting(posting._id)}
                              >
                                Approve
                              </button>
                              <button
                                className="admin-btn danger"
                                onClick={() => handleDeleteJobPosting(posting._id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {jobPostings.filter((p) => p.approvalStatus === 'rejected').length === 0 && (
                    <div className="admin-empty">No rejected job postings</div>
                  )}
                </div>

                <div className="admin-table-wrap" style={{ marginTop: 24 }}>
                  <h3>
                    Drafts ({jobPostings.filter((p) => !p.isPublished).length})
                  </h3>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Job</th>
                        <th>Company</th>
                        <th>Plan / pay</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobPostings
                        .filter((p) => !p.isPublished)
                        .map((posting) => (
                          <tr key={posting._id}>
                            <td>
                              <div className="admin-entity-cell">
                                <AdminMediaThumb
                                  src={posting.companyId?.logoUrl}
                                  label={posting.companyId?.name || posting.title}
                                />
                                <div>
                                  <div>{posting.title}</div>
                                  <div className="admin-muted">{previewDescription(posting.description)}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              {posting.companyId?.name || '—'}
                              <div className="admin-muted">{posting.userId?.email || ''}</div>
                            </td>
                            <td>
                              {posting.plan} · {posting.paymentStatus || 'draft'}
                            </td>
                            <td className="admin-actions">
                              <button
                                className="admin-btn danger"
                                onClick={() => handleDeleteJobPosting(posting._id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {jobPostings.filter((p) => !p.isPublished).length === 0 && (
                    <div className="admin-empty">No drafts</div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
